import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from "reactstrap";
import { AppContext } from '../application/provider';
import CajaFinancieroPresupuestoService from "../axios_services/cajafinancieropresupuesto.service";
import Loader from "../components/Loader/Loader";
import { ButtonSearch, ButtonUpdate, ButtonDelete, ButtonInsert } from '../components/Buttons/Buttons';
import ModalCajaFinancieroPresupuesto from '../components/Modal/ModalCajaFinancieroPresupuesto';
import './CajaFinancieroMovimiento.css';

function CajaFinancieroPresupuesto() {
    const [state] = useContext(AppContext);
    const [presupuestos, setPresupuestos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPresupuesto, setSelectedPresupuesto] = useState(null);
    const initialFetchDone = useRef(false);

    const [periodo_fecha, setPeriodoFecha] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const formatoSoles = (monto) => {
        const valor = new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN',
        }).format(monto);
        return valor;
    };

    useEffect(() => {
        if (!initialFetchDone.current) {
            loadInitialPageData();
            initialFetchDone.current = true;
        }
    }, []);

    const loadInitialPageData = async () => {
        setIsLoading(true);
        const perfil_id = state.user?.perfil_id;
        const usuario_id = state.user?.usuario_id;

        try {
            const response = await CajaFinancieroPresupuestoService.getCajaFinancieroPresupuesto(
                perfil_id,
                usuario_id,
                periodo_fecha
            );

            setPresupuestos(response.data || []);
        } catch (error) {
            setPresupuestos([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPresupuestos = async () => {
        setIsLoading(true);
        try {
            const response = await CajaFinancieroPresupuestoService.getCajaFinancieroPresupuesto(
                state.user?.perfil_id,
                state.user?.usuario_id,
                periodo_fecha
            );

            setPresupuestos(response.data || []);
        } catch (error) {
            setPresupuestos([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCalcularClick = async () => {
        await fetchPresupuestos();
    };

    const handleNuevoPresupuesto = () => {
        setSelectedPresupuesto(null);
        setIsModalOpen(true);
    };

    const handleEditarPresupuesto = (presupuesto) => {
        setSelectedPresupuesto(presupuesto);
        setIsModalOpen(true);
    };

    const handleEliminarPresupuesto = async (presupuestoId) => {
        if (!window.confirm('¿Está seguro que desea eliminar este presupuesto?')) {
            return;
        }

        try {
            setIsLoading(true);
            await CajaFinancieroPresupuestoService.deleteCajaFinancieroPresupuesto({
                caja_financiero_presupuesto_id: presupuestoId,
                perfil_id: state.user?.perfil_id
            });
            await fetchPresupuestos();
            alert('Presupuesto eliminado correctamente');
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al eliminar el presupuesto';
            alert(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPresupuesto(null);
    };

    const handleSavePresupuesto = async (presupuestoData) => {
        try {
            setLoadingModal(true);
            setIsModalOpen(false);

            const dataConPerfil = {
                ...presupuestoData,
                perfil_id: state.user?.perfil_id
            };

            let response;
            if (selectedPresupuesto) {
                response = await CajaFinancieroPresupuestoService.updateCajaFinancieroPresupuesto({
                    ...dataConPerfil,
                    caja_financiero_presupuesto_id: selectedPresupuesto.caja_financiero_presupuesto_id
                });
            } else {
                response = await CajaFinancieroPresupuestoService.insertCajaFinancieroPresupuesto(dataConPerfil);
            }

            await fetchPresupuestos();
            setSelectedPresupuesto(null);

            return response;
        } catch (error) {
            alert(error.response?.data?.message || 'Ocurrió un error al guardar.');
            throw error;
        } finally {
            setLoadingModal(false);
        }
    };

    if (isLoading || loadingModal) {
        return <Loader />;
    }

    return (
        <div className="caja-flujo-container">
            <div className="flex items-center justify-between mb-4">
                <h1>CAJA FINANCIERO - PRESUPUESTO</h1>
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <label htmlFor="periodo_fecha">Periodo:</label>
                    <input
                        id="periodo_fecha"
                        type="month"
                        value={periodo_fecha}
                        onChange={(e) => setPeriodoFecha(e.target.value)}
                    />
                </div>
            </div>

            <ButtonSearch
                onClick={handleCalcularClick}
                isLoading={isLoading}
            />

            <div className="nuevo-movimiento-section">
                {[8, 12, 13, 21].includes(Number(state.user?.perfil_id)) && (
                    <ButtonInsert onClick={handleNuevoPresupuesto} />
                )}
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>CATEGORÍA</th>
                            <th>CONCEPTO</th>
                            <th>MONTO</th>
                            {[8, 12, 13, 21].includes(Number(state.user?.perfil_id)) && <th>ACCIONES</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {presupuestos.length > 0 ? (
                            presupuestos.map((item, index) => (
                                <tr key={item.caja_financiero_presupuesto_id || index}>
                                    <td>{item.nombre_categoria || 'N/A'}</td>
                                    <td>{item.nombre_concepto || 'N/A'}</td>
                                    <td className={item.monto}>
                                        {formatoSoles(item.monto)}
                                    </td>
                                    {[8, 12, 13, 21].includes(Number(state.user?.perfil_id)) && (
                                        <td>
                                            <div className="acciones-container">
                                                <ButtonUpdate
                                                    onClick={() => handleEditarPresupuesto(item)}
                                                />
                                                <ButtonDelete
                                                    onClick={() => handleEliminarPresupuesto(item.caja_financiero_presupuesto_id)}
                                                />
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={[8, 12, 13, 21].includes(Number(state.user?.perfil_id)) ? 4 : 3}
                                    className="text-center text-gray-500 py-4">
                                    No hay datos disponibles para este período.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalCajaFinancieroPresupuesto
                onSave={handleSavePresupuesto}
                onClose={handleCloseModal}
                isOpen={isModalOpen}
                presupuesto={selectedPresupuesto}
                perfilId={state.user?.perfil_id}
                usuarioId={state.user?.usuario_id}
            />
        </div>
    );
}

export default CajaFinancieroPresupuesto;